import { getAtPath, parseOverrideKey } from "./recipe";
import type { KeyValuePair, SubmitDatasetSelection } from "./submitWizardForm";
import type { JsonObject } from "@/api";

/**
 * 어노테이션 세트 선택과 mask co-train 스위치가 어긋났을 때의 안내.
 *
 * 두 축은 서로를 모른다. 세트는 mask 를 **만들고**, config 키
 * `policy.co_train.reconstruct_segmentation_mask.enable` 이 그 mask 를 **쓴다**. 한쪽만 켜면
 * 알림 없이 아무 효과가 없거나(만들고 쓰지 않는다), 학습이 첫 step 에서 실패한다(쓰는데 없다).
 * 어느 쪽도 제출을 막지 않으므로 여기서 안내 문구로만 연결한다.
 *
 * **config 값을 대신 켜지 않는다**(사용자 확정). config 본문·override 는 config 섹션이
 * 소유한다. 위저드가 값을 설정하면 사용자가 끈 값을 다시 켜게 된다.
 *
 * BE 는 세트 선택과 관계없이 `segmentation_mask_source_unverified` warning 을 항상 준다(정적
 * 판정 불가). 이 안내는 그 warning 을 해석하는 방법을 알려 준다.
 */

export const MASK_CO_TRAIN_KEY = "policy.co_train.reconstruct_segmentation_mask.enable";

// YAML 의 참은 bool 이고 override 행의 참은 문자열이라 둘을 함께 받는다(BE `_is_true` 와 같은 규칙).
function isTrue(value: unknown): boolean {
  return value === true || value === "true" || value === "True";
}

/**
 * 제출될 config 에서 mask co-train 이 켜지는지 판정한다. override 가 본문보다 우선한다.
 *
 * 본문만 읽으면 ZERG-336 이 고친 오류를 반복한다. override 로 켠 값은 config 본문에 없다.
 * 같은 키를 여러 행에 적었으면 마지막 행이 우선한다(hydra 와 같은 규칙).
 */
export function maskCoTrainEnabled(input: {
  overrides: readonly KeyValuePair[];
  configBody: JsonObject | null;
}): boolean {
  const rows = input.overrides
    .map((pair) => ({ parsed: parseOverrideKey(pair.key.trim()), value: pair.value }))
    .filter((row) => row.parsed.path === MASK_CO_TRAIN_KEY);
  const last = rows.at(-1);
  if (last !== undefined) {
    // delete(`~`)는 값이 아니라 키를 지운다. 행에 남은 값 문자열로 on/off 를 판정하면
    // `~key` + 값 "true" 를 "켬"으로 읽어서 안내가 반대가 된다. 본문 폴백도 하지 않는다.
    // 지운 키는 본문에 있어도 제출될 config 에 없다.
    return last.parsed.op === "delete" ? false : isTrue(last.value.trim());
  }
  return input.configBody === null ? false : isTrue(getAtPath(input.configBody, MASK_CO_TRAIN_KEY));
}

/** 어긋남이 없으면 null. 두 축이 맞거나 둘 다 꺼져 있으면 안내할 내용이 없다. */
export function annotationMaskAdvice(input: {
  datasets: readonly SubmitDatasetSelection[];
  overrides: readonly KeyValuePair[];
  configBody: JsonObject | null;
}): string | null {
  const chosen = input.datasets.some((dataset) => dataset.annotationSetUuid !== null);
  const enabled = maskCoTrainEnabled(input);
  if (chosen && !enabled) {
    return `Annotation Set으로 mask 를 만들지만 이 config 는 mask 를 쓰지 않습니다. Training에 반영하려면 ${MASK_CO_TRAIN_KEY} 를 켜 주세요.`;
  }
  if (!chosen && enabled) {
    return `이 config 는 mask 를 쓰는데 고른 Annotation Set이 없습니다. Dataset 트리에 mask 가 없으면 Training이 첫 step 에서 멈춥니다. 세트를 고르면 registry 가 만들어 줍니다.`;
  }
  return null;
}
